import React, {
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  FiChevronRight,
  FiFolder,
  FiGitBranch,
  FiImage,
  FiSave,
  FiX,
} from "react-icons/fi";

import API from "../../../../../../utils/apiCalling";
import { config } from "../../../../../../utils/apiUrl";
import useSwal from "../../../../../../hooks/useSwal";

/* =========================================================
   INITIAL FORM
========================================================= */

const INITIAL_FORM = {
  name: "",
  slug: "",
  description: "",

  parentCategory: "",

  image: "",
  icon: "",
  bannerImage: "",

  sortOrder: 0,

  showInMenu: true,
  showOnHome: false,
  isFeatured: false,

  status: "active",

  metaTitle: "",
  metaDescription: "",
  metaKeywords: "",
};

/* =========================================================
   NORMALIZE ID
========================================================= */

const normalizeId = (
  value
) => {
  if (!value) {
    return "";
  }

  if (
    typeof value ===
    "string"
  ) {
    return value;
  }

  if (
    typeof value ===
    "object"
  ) {
    return (
      value._id ||
      value.id ||
      ""
    );
  }

  return String(
    value
  );
};

/* =========================================================
   CREATE SLUG
========================================================= */

const createSlug = (
  value
) =>
  String(
    value || ""
  )
    .trim()
    .toLowerCase()
    .replace(
      /[^a-z0-9]+/g,
      "-"
    )
    .replace(
      /^-+|-+$/g,
      ""
    );

/* =========================================================
   EXTRACT DROPDOWN CATEGORIES

   Supports:

   data: []

   OR

   data: {
     categories: []
   }

   OR

   categories: []
========================================================= */

const extractCategories = (
  response
) => {
  if (
    Array.isArray(
      response?.data
    )
  ) {
    return response.data;
  }

  if (
    Array.isArray(
      response?.data
        ?.categories
    )
  ) {
    return response.data
      .categories;
  }

  if (
    Array.isArray(
      response
        ?.categories
    )
  ) {
    return response
      .categories;
  }

  return [];
};

/* =========================================================
   COMPONENT
========================================================= */

const AddEditCategory = ({
  open,
  category,
  initialParentCategory = null,
  onClose,
  onSuccess,
}) => {
  const api =
    useMemo(
      () =>
        new API(),
      []
    );

  const swal =
    useSwal();

  /* =======================================================
     STATE
  ======================================================= */

  const [
    form,
    setForm,
  ] =
    useState({
      ...INITIAL_FORM,
    });

  const [
    allCategories,
    setAllCategories,
  ] =
    useState([]);

  const [
    loadingCategories,
    setLoadingCategories,
  ] =
    useState(false);

  const [
    saving,
    setSaving,
  ] =
    useState(false);

  const [
    error,
    setError,
  ] =
    useState("");

  const [
    slugTouched,
    setSlugTouched,
  ] =
    useState(false);

  /* =======================================================
     MODE
  ======================================================= */

  const isEditMode =
    Boolean(
      category?._id
    );

  const categoryId =
    normalizeId(
      category?._id
    );

  const selectedParentId =
    normalizeId(
      form.parentCategory
    );

  /* =======================================================
     INITIALIZE FORM
  ======================================================= */

  useEffect(
    () => {
      if (!open) {
        return;
      }

      /* ===================================================
         EDIT
      =================================================== */

      if (
        category?._id
      ) {
        const parentId =
          normalizeId(
            category
              ?.parentCategory
          );

        setForm({
          name:
            category?.name ||
            "",

          slug:
            category?.slug ||
            "",

          description:
            category
              ?.description ||
            "",

          parentCategory:
            parentId,

          image:
            category?.image ||
            "",

          icon:
            category?.icon ||
            "",

          bannerImage:
            category
              ?.bannerImage ||
            "",

          sortOrder:
            Number(
              category
                ?.sortOrder ??
              0
            ),

          showInMenu:
            category
              ?.showInMenu !==
            false,

          showOnHome:
            Boolean(
              category
                ?.showOnHome
            ),

          isFeatured:
            Boolean(
              category
                ?.isFeatured
            ),

          status:
            category?.status ||
            "active",

          metaTitle:
            category
              ?.metaTitle ||
            "",

          metaDescription:
            category
              ?.metaDescription ||
            "",

          metaKeywords:
            Array.isArray(
              category
                ?.metaKeywords
            )
              ? category
                  .metaKeywords
                  .join(", ")
              : category
                  ?.metaKeywords ||
                "",
        });

        setSlugTouched(
          true
        );

        setError("");

        return;
      }

      /* ===================================================
         CREATE

         Add Category:
         parentCategory = ""

         + Child:
         parentCategory = clicked category
      =================================================== */

      const initialParentId =
        normalizeId(
          initialParentCategory
            ?._id ||
          initialParentCategory
        );

      setForm({
        ...INITIAL_FORM,

        parentCategory:
          initialParentId,
      });

      setSlugTouched(
        false
      );

      setError("");
    },
    [
      open,
      category,
      initialParentCategory,
    ]
  );

  /* =======================================================
     LOAD CATEGORY DROPDOWN

     IMPORTANT:

     Use dedicated dropdown API.

     During edit:
     excludeCategoryId tells backend to exclude:

     1. current category
     2. its descendants

     assuming controller implements the hierarchy protection
     we created earlier.
  ======================================================= */

  useEffect(
    () => {
      if (!open) {
        return;
      }

      let mounted =
        true;

      const loadCategories =
        async () => {
          try {
            setLoadingCategories(
              true
            );

            /*
             * IMPORTANT:
             *
             * If your config property has a
             * different name, only change
             * this config property.
             */

            const response =
              await api.post(
                config
                  .getInventoryCategoryDropdown,

                {
                  includeInactive:
                    true,

                  excludeCategoryId:
                    isEditMode
                      ? categoryId
                      : "",
                }
              );

            if (
              !mounted
            ) {
              return;
            }

            const records =
              extractCategories(
                response
              );

            setAllCategories(
              records
            );
          } catch (
            requestError
          ) {
            console.error(
              "Load category dropdown error:",
              requestError
            );

            if (
              mounted
            ) {
              setAllCategories(
                []
              );
            }
          } finally {
            if (
              mounted
            ) {
              setLoadingCategories(
                false
              );
            }
          }
        };

      loadCategories();

      return () => {
        mounted =
          false;
      };
    },
    [
      open,
      api,
      isEditMode,
      categoryId,
    ]
  );

  /* =======================================================
     AVAILABLE PARENTS

     Frontend safety check.

     Backend must still perform the real validation.
  ======================================================= */

  const availableParents =
    useMemo(
      () => {
        return allCategories.filter(
          (
            item
          ) => {
            const itemId =
              normalizeId(
                item?._id
              );

            if (
              !itemId
            ) {
              return false;
            }

            /*
             * Current category can
             * never be its own parent.
             */

            if (
              isEditMode &&
              itemId ===
                categoryId
            ) {
              return false;
            }

            return true;
          }
        );
      },
      [
        allCategories,
        isEditMode,
        categoryId,
      ]
    );

  /* =======================================================
     SELECTED PARENT
  ======================================================= */

  const selectedParent =
    useMemo(
      () =>
        allCategories.find(
          (
            item
          ) =>
            normalizeId(
              item?._id
            ) ===
            selectedParentId
        ) ||
        null,
      [
        allCategories,
        selectedParentId,
      ]
    );

  /* =======================================================
     EFFECTIVE PARENT

     Needed because when "+ Child"
     opens the modal, dropdown API
     may still be loading.
  ======================================================= */

  const effectiveParent =
    selectedParent ||
    (
      initialParentCategory &&
      normalizeId(
        initialParentCategory
          ?._id
      ) ===
        selectedParentId
        ? initialParentCategory
        : null
    );

  /* =======================================================
     CALCULATED LEVEL

     Backend remains authoritative.

     This calculation is mainly
     for UI preview.

     Root             = 0
     Root child       = 1
     Grandchild       = 2
     etc.
  ======================================================= */

  const calculatedLevel =
    useMemo(
      () => {
        if (
          !selectedParentId
        ) {
          return 0;
        }

        return (
          Number(
            effectiveParent
              ?.level ??
            0
          ) + 1
        );
      },
      [
        selectedParentId,
        effectiveParent,
      ]
    );

  /* =======================================================
     INPUT CHANGE
  ======================================================= */

  const handleChange =
    (
      event
    ) => {
      const {
        name,
        value,
        type,
        checked,
      } =
        event.target;

      const nextValue =
        type ===
        "checkbox"
          ? checked
          : value;

      setForm(
        (
          previous
        ) => ({
          ...previous,

          [name]:
            nextValue,
        })
      );

      /* ===================================================
         AUTO SLUG
      =================================================== */

      if (
        name ===
          "name" &&
        !slugTouched
      ) {
        setForm(
          (
            previous
          ) => ({
            ...previous,

            name:
              value,

            slug:
              createSlug(
                value
              ),
          })
        );
      }

      if (
        name ===
        "slug"
      ) {
        setSlugTouched(
          true
        );
      }

      if (
        error
      ) {
        setError("");
      }
    };

  /* =======================================================
     META KEYWORDS
  ======================================================= */

  const parseMetaKeywords =
    () =>
      String(
        form.metaKeywords ||
        ""
      )
        .split(",")
        .map(
          (
            item
          ) =>
            item.trim()
        )
        .filter(
          Boolean
        );

  /* =======================================================
     VALIDATION
  ======================================================= */

  const validateForm =
    () => {
      const name =
        String(
          form.name ||
          ""
        ).trim();

      if (!name) {
        return "Category name is required.";
      }

      if (
        name.length < 2
      ) {
        return "Category name must contain at least 2 characters.";
      }

      const slug =
        createSlug(
          form.slug ||
          form.name
        );

      if (!slug) {
        return "Category slug is required.";
      }

      /*
       * Frontend self-parent safety.
       */

      if (
        isEditMode &&
        selectedParentId &&
        selectedParentId ===
          categoryId
      ) {
        return "A category cannot be its own parent.";
      }

      return "";
    };

  /* =======================================================
     BUILD FORMDATA

     Backend routes use:

     categoryImageUpload

     so FormData keeps this endpoint
     compatible with multipart/form-data.
  ======================================================= */

  const buildFormData =
    () => {
      const formData =
        new FormData();

      /*
       * UPDATE route expects id
       * inside req.body.id
       */

      if (
        isEditMode
      ) {
        formData.append(
          "id",
          categoryId
        );
      }

      formData.append(
        "name",
        form.name.trim()
      );

      formData.append(
        "slug",
        createSlug(
          form.slug ||
          form.name
        )
      );

      formData.append(
        "description",
        String(
          form.description ||
          ""
        ).trim()
      );

      /*
       * Empty parent means ROOT.
       *
       * We send an empty string because
       * multipart/form-data cannot send
       * JavaScript null directly.
       *
       * Controller should convert:
       *
       * "" => null
       */

      formData.append(
        "parentCategory",
        selectedParentId ||
          ""
      );

      /*
       * Backend should calculate level.
       *
       * This value is still useful
       * if your controller currently
       * accepts it.
       */

      formData.append(
        "level",
        String(
          calculatedLevel
        )
      );

      formData.append(
        "image",
        String(
          form.image ||
          ""
        ).trim()
      );

      formData.append(
        "icon",
        String(
          form.icon ||
          ""
        ).trim()
      );

      formData.append(
        "bannerImage",
        String(
          form.bannerImage ||
          ""
        ).trim()
      );

      formData.append(
        "sortOrder",
        String(
          Math.max(
            Number(
              form.sortOrder ||
              0
            ),
            0
          )
        )
      );

      formData.append(
        "showInMenu",
        String(
          Boolean(
            form.showInMenu
          )
        )
      );

      formData.append(
        "showOnHome",
        String(
          Boolean(
            form.showOnHome
          )
        )
      );

      formData.append(
        "isFeatured",
        String(
          Boolean(
            form.isFeatured
          )
        )
      );

      formData.append(
        "status",
        form.status
      );

      formData.append(
        "metaTitle",
        String(
          form.metaTitle ||
          ""
        ).trim()
      );

      formData.append(
        "metaDescription",
        String(
          form.metaDescription ||
          ""
        ).trim()
      );

      /*
       * Send JSON because FormData
       * cannot send arrays directly.
       */

      formData.append(
        "metaKeywords",
        JSON.stringify(
          parseMetaKeywords()
        )
      );

      return formData;
    };

  /* =======================================================
     SAVE
  ======================================================= */

  const handleSubmit =
    async (
      event
    ) => {
      event.preventDefault();

      if (
        saving
      ) {
        return;
      }

      const validationError =
        validateForm();

      if (
        validationError
      ) {
        setError(
          validationError
        );

        return;
      }

      try {
        setSaving(
          true
        );

        setError("");

        const formData =
          buildFormData();

        let response;

        /* =================================================
           UPDATE

           Backend:

           POST
           /inventory/categories/update

           req.body.id
        ================================================= */

        if (
          isEditMode
        ) {
          response =
            await api.postFormData(
              config
                .updateInventoryCategory,

              formData
            );
        }

        /* =================================================
           CREATE
        ================================================= */

        else {
          response =
            await api.postFormData(
              config
                .createInventoryCategory,

              formData
            );
        }

        /* =================================================
           RESPONSE
        ================================================= */

        if (
          response?.status !==
            "success" &&
          response?.success !==
            true
        ) {
          throw new Error(
            response?.message ||
            (
              isEditMode
                ? "Unable to update category."
                : "Unable to create category."
            )
          );
        }

        await swal.success({
          icon:
            "success",

          title:
            isEditMode
              ? "Category Updated"
              : selectedParentId
                ? "Subcategory Created"
                : "Category Created",

          text:
            response?.message ||
            (
              isEditMode
                ? "Category updated successfully."
                : "Category created successfully."
            ),

          timer:
            1400,

          showConfirmButton:
            false,
        });

        onSuccess?.(
          response
        );
      } catch (
        requestError
      ) {
        console.error(
          "Save category error:",
          requestError
        );

        const message =
          requestError
            ?.response
            ?.data
            ?.message ||
          requestError
            ?.message ||
          (
            isEditMode
              ? "Unable to update category."
              : "Unable to create category."
          );

        setError(
          message
        );

        await swal.error({
          icon:
            "error",

          title:
            isEditMode
              ? "Update Failed"
              : "Create Failed",

          text:
            message,
        });
      } finally {
        setSaving(
          false
        );
      }
    };

  /* =======================================================
     CLOSE
  ======================================================= */

  const handleClose =
    () => {
      if (
        saving
      ) {
        return;
      }

      onClose?.();
    };

  /* =======================================================
     DON'T RENDER
  ======================================================= */

  if (!open) {
    return null;
  }

  /* =======================================================
     RENDER
  ======================================================= */

  return (
    <div
      className="inventory-category-modal-overlay"
      onMouseDown={
        (
          event
        ) => {
          if (
            event.target ===
            event.currentTarget
          ) {
            handleClose();
          }
        }
      }
    >
      <div
        className="inventory-category-modal"
      >
        {/* ===============================================
            HEADER
        =============================================== */}

        <div
          className="inventory-category-modal-header"
        >
          <div
            className="inventory-category-modal-title"
          >
            <div
              className="inventory-category-modal-icon"
            >
              {isEditMode ? (
                <FiFolder />
              ) : selectedParentId ? (
                <FiGitBranch />
              ) : (
                <FiFolder />
              )}
            </div>

            <div>
              <span>
                {isEditMode
                  ? "EDIT CATEGORY"
                  : selectedParentId
                    ? "ADD SUBCATEGORY"
                    : "ADD CATEGORY"}
              </span>

              <h3>
                {isEditMode
                  ? "Update Category"
                  : selectedParentId
                    ? `Add under ${
                        effectiveParent
                          ?.name ||
                        "selected category"
                      }`
                    : "Create New Category"}
              </h3>

              <p>
                {isEditMode
                  ? "Update category information and hierarchy."
                  : selectedParentId
                    ? "Create a nested category under the selected parent."
                    : "Create a new root category for your inventory."}
              </p>
            </div>
          </div>

          <button
            type="button"
            className="inventory-category-modal-close"
            onClick={
              handleClose
            }
            disabled={
              saving
            }
          >
            <FiX />
          </button>
        </div>

        {/* ===============================================
            FORM
        =============================================== */}

        <form
          onSubmit={
            handleSubmit
          }
        >
          <div
            className="inventory-category-modal-body"
          >
            {error ? (
              <div
                className="inventory-category-form-error"
              >
                {error}
              </div>
            ) : null}

            {/* ===========================================
                HIERARCHY PREVIEW
            =========================================== */}

            <div
              className="inventory-category-hierarchy-preview"
            >
              <div>
                <FiGitBranch />

                <span>
                  Category Hierarchy
                </span>
              </div>

              <div
                className="inventory-category-hierarchy-path"
              >
                {!selectedParentId ? (
                  <>
                    <strong>
                      Root Category
                    </strong>

                    <span>
                      Level 0
                    </span>
                  </>
                ) : (
                  <>
                    <strong>
                      {effectiveParent
                        ?.path ||
                        effectiveParent
                          ?._treePath ||
                        effectiveParent
                          ?.name ||
                        "Selected Parent"}

                      <FiChevronRight />

                      {form.name ||
                        "New Category"}
                    </strong>

                    <span>
                      Level{" "}
                      {
                        calculatedLevel
                      }
                    </span>
                  </>
                )}
              </div>
            </div>

            {/* ===========================================
                BASIC INFORMATION
            =========================================== */}

            <section
              className="inventory-category-form-section"
            >
              <div
                className="inventory-category-form-section-heading"
              >
                <h4>
                  Basic Information
                </h4>

                <p>
                  Enter category name,
                  description and URL slug.
                </p>
              </div>

              <div
                className="inventory-category-form-grid"
              >
                <div
                  className="inventory-category-form-group"
                >
                  <label>
                    Category Name
                    <span>
                      *
                    </span>
                  </label>

                  <input
                    type="text"
                    name="name"
                    value={
                      form.name
                    }
                    onChange={
                      handleChange
                    }
                    placeholder="e.g. Laptops"
                    maxLength={
                      100
                    }
                    autoFocus
                  />
                </div>

                <div
                  className="inventory-category-form-group"
                >
                  <label>
                    Slug
                    <span>
                      *
                    </span>
                  </label>

                  <input
                    type="text"
                    name="slug"
                    value={
                      form.slug
                    }
                    onChange={
                      handleChange
                    }
                    placeholder="e.g. laptops"
                  />
                </div>

                <div
                  className="inventory-category-form-group inventory-category-form-full"
                >
                  <label>
                    Description
                  </label>

                  <textarea
                    name="description"
                    value={
                      form.description
                    }
                    onChange={
                      handleChange
                    }
                    placeholder="Describe this category..."
                    rows={
                      4
                    }
                    maxLength={
                      2000
                    }
                  />
                </div>
              </div>
            </section>

            {/* ===========================================
                HIERARCHY
            =========================================== */}

            <section
              className="inventory-category-form-section"
            >
              <div
                className="inventory-category-form-section-heading"
              >
                <h4>
                  Category Hierarchy
                </h4>

                <p>
                  Select any valid existing
                  category as the parent.
                </p>
              </div>

              <div
                className="inventory-category-form-grid"
              >
                <div
                  className="inventory-category-form-group"
                >
                  <label>
                    Parent Category
                  </label>

                  <select
                    name="parentCategory"
                    value={
                      selectedParentId
                    }
                    onChange={
                      handleChange
                    }
                    disabled={
                      loadingCategories
                    }
                  >
                    <option
                      value=""
                    >
                      {loadingCategories
                        ? "Loading categories..."
                        : "No Parent — Root Category"}
                    </option>

                    {/* ===================================
                        + CHILD FALLBACK

                        Keep selected parent visible
                        while dropdown is loading.
                    =================================== */}

                    {initialParentCategory &&
                    selectedParentId &&
                    !availableParents.some(
                      (
                        item
                      ) =>
                        normalizeId(
                          item?._id
                        ) ===
                        selectedParentId
                    ) ? (
                      <option
                        value={
                          selectedParentId
                        }
                      >
                        {initialParentCategory
                          ?.path ||
                          initialParentCategory
                            ?.name ||
                          "Selected Parent"}
                      </option>
                    ) : null}

                    {/* ===================================
                        EDIT EXISTING PARENT FALLBACK
                    =================================== */}

                    {isEditMode &&
                    selectedParentId &&
                    category
                      ?.parentCategory &&
                    !availableParents.some(
                      (
                        item
                      ) =>
                        normalizeId(
                          item?._id
                        ) ===
                        selectedParentId
                    ) ? (
                      <option
                        value={
                          selectedParentId
                        }
                      >
                        {typeof category
                          .parentCategory ===
                        "object"
                          ? category
                              .parentCategory
                              ?.name ||
                            "Current Parent"
                          : "Current Parent"}
                      </option>
                    ) : null}

                    {availableParents.map(
                      (
                        item
                      ) => {
                        const depth =
                          Number(
                            item
                              ?.level ??
                            item
                              ?._treeDepth ??
                            0
                          );

                        const prefix =
                          depth > 0
                            ? `${"— ".repeat(
                                depth
                              )}`
                            : "";

                        const label =
                          item?.path ||
                          item
                            ?._treePath ||
                          item?.name ||
                          "Category";

                        return (
                          <option
                            key={
                              item._id
                            }
                            value={
                              item._id
                            }
                          >
                            {prefix}
                            {label}
                          </option>
                        );
                      }
                    )}
                  </select>

                  <small>
                    Leave empty to create
                    a root category.
                  </small>
                </div>

                <div
                  className="inventory-category-form-group"
                >
                  <label>
                    Category Level
                  </label>

                  <div
                    className="inventory-category-level-display"
                  >
                    <FiGitBranch />

                    <strong>
                      Level{" "}
                      {
                        calculatedLevel
                      }
                    </strong>

                    <span>
                      {calculatedLevel ===
                      0
                        ? "Root"
                        : "Nested"}
                    </span>
                  </div>
                </div>
              </div>
            </section>

            {/* ===========================================
                DISPLAY SETTINGS
            =========================================== */}

            <section
              className="inventory-category-form-section"
            >
              <div
                className="inventory-category-form-section-heading"
              >
                <h4>
                  Display Settings
                </h4>

                <p>
                  Configure category images
                  and storefront visibility.
                </p>
              </div>

              <div
                className="inventory-category-form-grid"
              >
                <div
                  className="inventory-category-form-group"
                >
                  <label>
                    <FiImage />

                    Category Image
                  </label>

                  <input
                    type="text"
                    name="image"
                    value={
                      form.image
                    }
                    onChange={
                      handleChange
                    }
                    placeholder="Image URL"
                  />
                </div>

                <div
                  className="inventory-category-form-group"
                >
                  <label>
                    Icon
                  </label>

                  <input
                    type="text"
                    name="icon"
                    value={
                      form.icon
                    }
                    onChange={
                      handleChange
                    }
                    placeholder="Icon URL or icon name"
                  />
                </div>

                <div
                  className="inventory-category-form-group inventory-category-form-full"
                >
                  <label>
                    Banner Image
                  </label>

                  <input
                    type="text"
                    name="bannerImage"
                    value={
                      form.bannerImage
                    }
                    onChange={
                      handleChange
                    }
                    placeholder="Banner image URL"
                  />
                </div>

                <div
                  className="inventory-category-form-group"
                >
                  <label>
                    Sort Order
                  </label>

                  <input
                    type="number"
                    name="sortOrder"
                    min="0"
                    value={
                      form.sortOrder
                    }
                    onChange={
                      handleChange
                    }
                  />
                </div>

                <div
                  className="inventory-category-form-group"
                >
                  <label>
                    Status
                  </label>

                  <select
                    name="status"
                    value={
                      form.status
                    }
                    onChange={
                      handleChange
                    }
                  >
                    <option
                      value="active"
                    >
                      Active
                    </option>

                    <option
                      value="inactive"
                    >
                      Inactive
                    </option>
                  </select>
                </div>
              </div>

              <div
                className="inventory-category-checkbox-grid"
              >
                <label>
                  <input
                    type="checkbox"
                    name="showInMenu"
                    checked={
                      form.showInMenu
                    }
                    onChange={
                      handleChange
                    }
                  />

                  <span>
                    <strong>
                      Show in Menu
                    </strong>

                    Display this category
                    in storefront navigation.
                  </span>
                </label>

                <label>
                  <input
                    type="checkbox"
                    name="showOnHome"
                    checked={
                      form.showOnHome
                    }
                    onChange={
                      handleChange
                    }
                  />

                  <span>
                    <strong>
                      Show on Home
                    </strong>

                    Display category on
                    homepage.
                  </span>
                </label>

                <label>
                  <input
                    type="checkbox"
                    name="isFeatured"
                    checked={
                      form.isFeatured
                    }
                    onChange={
                      handleChange
                    }
                  />

                  <span>
                    <strong>
                      Featured
                    </strong>

                    Mark category as
                    featured.
                  </span>
                </label>
              </div>
            </section>

            {/* ===========================================
                SEO
            =========================================== */}

            <section
              className="inventory-category-form-section"
            >
              <div
                className="inventory-category-form-section-heading"
              >
                <h4>
                  SEO Settings
                </h4>

                <p>
                  Optional search engine
                  metadata.
                </p>
              </div>

              <div
                className="inventory-category-form-grid"
              >
                <div
                  className="inventory-category-form-group inventory-category-form-full"
                >
                  <label>
                    Meta Title
                  </label>

                  <input
                    type="text"
                    name="metaTitle"
                    value={
                      form.metaTitle
                    }
                    onChange={
                      handleChange
                    }
                    placeholder="SEO title"
                  />
                </div>

                <div
                  className="inventory-category-form-group inventory-category-form-full"
                >
                  <label>
                    Meta Description
                  </label>

                  <textarea
                    name="metaDescription"
                    value={
                      form.metaDescription
                    }
                    onChange={
                      handleChange
                    }
                    placeholder="SEO description"
                    rows={
                      3
                    }
                  />
                </div>

                <div
                  className="inventory-category-form-group inventory-category-form-full"
                >
                  <label>
                    Meta Keywords
                  </label>

                  <input
                    type="text"
                    name="metaKeywords"
                    value={
                      form.metaKeywords
                    }
                    onChange={
                      handleChange
                    }
                    placeholder="laptop, computer, accessories"
                  />

                  <small>
                    Separate keywords using
                    commas.
                  </small>
                </div>
              </div>
            </section>
          </div>

          {/* =============================================
              FOOTER
          ============================================= */}

          <div
            className="inventory-category-modal-footer"
          >
            <button
              type="button"
              className="inventory-category-form-cancel"
              onClick={
                handleClose
              }
              disabled={
                saving
              }
            >
              Cancel
            </button>

            <button
              type="submit"
              className="inventory-category-form-save"
              disabled={
                saving
              }
            >
              <FiSave />

              {saving
                ? "Saving..."
                : isEditMode
                  ? "Update Category"
                  : selectedParentId
                    ? "Create Subcategory"
                    : "Create Category"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddEditCategory;